import React, { useState, useEffect, useCallback } from 'react';
import { Scenario } from '../types';
import { useToast } from '../contexts/ToastContext';

interface AddScenarioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (scenario: Partial<Scenario>) => void;
  categories: string[];
  allScenarios: Scenario[];
}

const AddScenarioModal: React.FC<AddScenarioModalProps> = ({ isOpen, onClose, onSubmit, categories, allScenarios }) => {
  const { showToast } = useToast();
  const [newScenario, setNewScenario] = useState<Partial<Scenario>>({
    'Kategoria': '',
    'ID testu': '',
    'Nazwa testu': '',
    'Kroki testowe': '',
    'Oczekiwany rezultat': ''
  });
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Funkcja generująca sugerowane ID testu na podstawie istniejących ID w wybranej kategorii
  const generateIdFromExisting = useCallback((category: string): string => {
    if (!category) return 'np. LOGIN001';
    
    // Filtruj scenariusze tylko dla wybranej kategorii
    const categoryScenarios = allScenarios.filter(s => s.Kategoria === category);
    
    // Znajdź wszystkie ID testów w wybranej kategorii
    const existingIds = categoryScenarios.map(s => s['ID testu']);
    
    // Znajdź ID z najwyższym numerem
    let highestNumber = 0;
    let lastId = '';
    let numberFormat = '';

    // Analizujemy istniejące ID, aby znaleźć wzór
    existingIds.forEach(id => {
      // Znajdź prefiks (litery) i numer w ID, uwzględniając myślniki
      const match = id.match(/^([A-Za-z-]+)(\d+)$/);
      if (match) {
        const [, , numberStr] = match;
        const number = parseInt(numberStr, 10);
        
        // Aktualizuj najwyższy numer
        if (number > highestNumber) {
          highestNumber = number;
          lastId = id;
          numberFormat = numberStr;
        }
      }
    });

    // Jeśli nie znaleziono żadnego ID w kategorii, zwróć placeholder
    if (!lastId) {
      return 'np. LOGIN001';
    }

    // Zwiększ najwyższy numer o 1 i sformatuj z wiodącymi zerami
    const nextNumber = highestNumber + 1;
    const paddingLength = numberFormat.length;
    const paddedNumber = String(nextNumber).padStart(paddingLength, '0');
    
    // Użyj tego samego prefiksu co w ostatnim ID
    const lastPrefix = lastId.match(/^([A-Za-z-]+)/)?.[1] || '';
    return `${lastPrefix}${paddedNumber}`;
  }, [allScenarios]);

  useEffect(() => {
    if (!isOpen) return;
    // Resetuj formularz po otwarciu
    const initialCategory = categories[0] || '';
    const initialId = generateIdFromExisting(initialCategory);
    setNewScenario({
      'Kategoria': initialCategory,
      'ID testu': initialId,
      'Nazwa testu': '',
      'Kroki testowe': '',
      'Oczekiwany rezultat': ''
    });
    setIsNewCategory(false);
    setNewCategoryName('');
  }, [isOpen, categories, allScenarios, generateIdFromExisting]);

  const handleChange = (field: keyof Scenario, value: string) => {
    if (field === 'Kategoria') {
      if (value === '__new__') {
        setIsNewCategory(true);
        setNewScenario({
          ...newScenario,
          [field]: '',
          'ID testu': ''
        });
      } else {
        const scenarioId = generateIdFromExisting(value);
        setNewScenario({
          ...newScenario,
          [field]: value,
          'ID testu': scenarioId
        });
        setIsNewCategory(false);
      }
    } else {
      setNewScenario({
        ...newScenario,
        [field]: value
      });
    }
  };

  const handleNewCategoryChange = (value: string) => {
    setNewCategoryName(value);
    // Aktualizujemy pole Kategoria tylko gdy mamy faktyczną wartość
    if (value.trim()) {
      const scenarioId = generateIdFromExisting(value);
      setNewScenario({
        ...newScenario,
        'Kategoria': value,
        'ID testu': scenarioId
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newScenario['Kategoria']) {
      showToast('Wybierz lub wprowadź nazwę kategorii', 'warning');
      return;
    }

    const scenarioId = generateIdFromExisting(newScenario['Kategoria']);
    if (allScenarios.some((s: Scenario) => s['ID testu'] === scenarioId)) {
      showToast(`Scenariusz o ID "${scenarioId}" już istnieje. Użyj innego ID.`, 'error');
      return;
    }
    
    // Ustaw wygenerowane ID w newScenario
    const scenarioWithId = {
      ...newScenario,
      'ID testu': scenarioId
    };
    
    onSubmit(scenarioWithId);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 dark:bg-black/60 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div 
          className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full"
          role="dialog" 
          aria-modal="true" 
          aria-labelledby="modal-headline"
        >
          <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="mb-4">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white" id="modal-headline">
                Dodaj nowy scenariusz testowy
              </h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kategoria
                  </label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={isNewCategory ? '__new__' : newScenario['Kategoria']}
                    onChange={(e) => handleChange('Kategoria', e.target.value)}
                    required
                  >
                    <option value="">Wybierz kategorię...</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                    <option value="__new__">+ Dodaj nową kategorię</option>
                  </select>
                  {isNewCategory && (
                    <input
                      type="text"
                      className="w-full mt-2 px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Wprowadź nazwę nowej kategorii"
                      value={newCategoryName}
                      onChange={(e) => handleNewCategoryChange(e.target.value)}
                      required
                    />
                  )}
                </div>
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ID testu
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newScenario['ID testu']}
                    onChange={(e) => handleChange('ID testu', e.target.value)}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nazwa testu
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Opisowa nazwa scenariusza testowego"
                    value={newScenario['Nazwa testu']}
                    onChange={(e) => handleChange('Nazwa testu', e.target.value)}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kroki testowe
                  </label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                    placeholder="Pierwszy krok&#10;Drugi krok&#10;Trzeci krok"
                    value={newScenario['Kroki testowe']}
                    onChange={(e) => handleChange('Kroki testowe', e.target.value)}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Oczekiwany rezultat
                  </label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-100 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                    placeholder="Opisz oczekiwany rezultat wykonania kroków testowych"
                    value={newScenario['Oczekiwany rezultat']}
                    onChange={(e) => handleChange('Oczekiwany rezultat', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="mt-8 flex justify-end gap-4">
                <button
                  type="button"
                  className="px-6 py-3 bg-gray-100 dark:bg-slate-600 text-gray-800 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-500 transition-all duration-200"
                  onClick={onClose}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200"
                >
                  Dodaj scenariusz
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddScenarioModal; 